import React from 'react';
import styled, { css } from 'styled-components';
import { ResultsProductCard } from '../../components/landing/results/ResultsProductCard';
import { ImpressionCard } from '../components/ImpressionCard';
import { CardSaveDrawer } from '../board/CardSaveDrawer';
import { MoreLikeThisSticker } from '../board/MoreLikeThisSticker';
import { HUB_GUTTER_CSS } from '../hub/hubType';
import type { GuideCardItem } from './guideTypes';

// Preview: one card per row on phones, four across on desktop (PRD Guide pages).
// Legacy keeps its existing 1/2/3-column layout.
//
// `$wideGutter`: THE-1161's full-width gutter now rides the SAME Gift Guide
// hub gate as `$hubFrame` (Kate, 2026-10-28, PR #883: no preview switch of
// its own) — `GuideBoardView.tsx` reads it via `useHubWideGutterFrame()`
// (`HubFrameContext.ts`), which `HubNavigationFrame.tsx` sets to the SAME
// value as `HubFrameContext` itself. Kept as its own context read rather
// than reusing `$hubFrame` directly only so this file and the rest of
// `HubFrameContext.ts`'s consumers need no change if the two gates are ever
// decoupled again.
// `$hubFrame` alone still decides which BRANCH renders (the 4-up hub grid
// below vs. the legacy 1/2/3-column one); `$wideGutter` only decides which
// gutter geometry `HUB_GUTTER_CSS` gives the hub branch once it's showing.
export const GuideProductGrid = styled.div<{ $hubFrame?: boolean; $wideGutter?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  padding: 16px 12px 8px;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  ${({ $hubFrame }) =>
    $hubFrame
      ? css`
          /* THE-1161: this rule's own base max-width: 1080px; margin: 0 auto
             (above) used to be overridden here by HUB_GUTTER_CSS's OLD
             max-width: 1400px; margin: 0 auto — same trick, bigger cap, so
             the hub frame never saw the 1080px box. HUB_GUTTER_CSS dropped
             its cap entirely (it's page-width padding now, not a centered
             box), so the 1080px base rule was left standing and the hub
             grid silently reverted to a centered 1080px island drifting
             right of the page title, which reads from PAGE_GUTTER_DESKTOP
             with no cap of its own. Un-cap explicitly instead of relying on
             an override that no longer exists. */
          max-width: none;
          margin: 0;
          ${HUB_GUTTER_CSS}
          padding-top: 16px;
          padding-bottom: 8px;
          @media (min-width: 1024px) {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        `
      : css`
          @media (min-width: 640px) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 20px;
          }
          @media (min-width: 1024px) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        `}
`;

// ImpressionCard (not a bare div) so every guide card feeds the ranker's
// first-party `product_impression` + dwell sink — guides are the marketing
// landers, and before this the sink saw guide saves but never the exposure
// that produced them.
//
// THE-515 deleted the tile's 'More actions' menu outright, and the guide
// renders ResultsProductCard without `onDismiss`, so there is no ⋯ and no X
// here to hide.
//
// The card's own top-right HEART is hidden, though — same rule, same reason as
// the board feed (THE-684): the guide's Save affordance is now the connected
// `CardSaveDrawer` bar below the photo, and two save controls on one card is
// one too many. `:not([data-board-save])` spares that bar, whose label is
// "Save" too; the heart's label flips to "Unsave" once saved, hence both.
// `display: none` (not visibility/opacity) so the hidden heart leaves the
// accessibility tree as well as the paint — the drawer is the only Save a
// screen reader hears.
const CardWrap = styled(ImpressionCard)`
  position: relative;

  & button[aria-label='Save']:not([data-board-save]),
  & button[aria-label='Unsave']:not([data-board-save]) {
    display: none !important;
  }
`;


/** Shared guide/collection card: one Save entry point and one similar-gifts entry point. */
export function GuideProductCard({ item, saved, priority, onOpen, onSave, onMoreLikeThis, impressionParams }: {
  item: GuideCardItem; saved: boolean; priority?: boolean;
  onOpen: () => void; onSave: () => void; onMoreLikeThis: () => void;
  impressionParams?: React.ComponentProps<typeof ImpressionCard>['impressionParams'];
}) {
  return <CardWrap data-product-id={item.id} impressionParams={impressionParams}>
    <ResultsProductCard item={item} liked={saved} priority={priority} wideImage
      onClick={onOpen} onSaveClick={undefined}
      imageOverlay={<MoreLikeThisSticker title={item.title} onClick={onMoreLikeThis} position="bottom-left" />}
      imageFooter={<CardSaveDrawer saved={saved} onClick={event => { event.stopPropagation(); onSave(); }} />} />
  </CardWrap>;
}
